export default {
  copyEvaluations: `
    <h2>1. Descripción General</h2>
    <p>
      Se incorpora una funcionalidad que permite copiar {evaluations} entre distintas asignaturas de forma
      bidireccional. Esto facilita la creación de estructuras de evaluación reutilizando las ya existentes
      en otras asignaturas vinculadas al mismo perfil de egreso o a la {baseMatter}, evitando la creación
      manual repetitiva.
    </p>
    <p>
      La funcionalidad se presenta mediante un modal de tres columnas (Origen, Destino y Vista Previa) que
      permite al usuario seleccionar qué {evaluations} copiar, cuáles eliminar o conservar del destino, y
      ajustar las ponderaciones antes de confirmar la operación.
    </p>

    <h2>2. Operaciones de Copia Disponibles</h2>
    <p>
      Existen tres operaciones de copia, cada una accesible desde un contexto diferente. Todas comparten el
      mismo modal reutilizable con capacidad bidireccional.
    </p>

    <h3>2.1 Copiar entre {egressProfileMatters} ({egressProfileMatters} a {egressProfileMatters})</h3>
    <p>
      Permite copiar {evaluations} desde la asignatura actual (dentro de un perfil de egreso) hacia otra
      asignatura vinculada a un perfil de egreso diferente, siempre que ambas compartan la misma {baseMatter}.
    </p>
    <p><strong>Condiciones de visibilidad:</strong></p>
    <ul>
      <li>Debe existir al menos otra asignatura del mismo tipo no vinculada en otro perfil de egreso.</li>
      <li>El usuario debe tener permisos de edición y ser super usuario.</li>
    </ul>
    <p>
      <strong>Dónde se encuentra:</strong> En la vista de {matterProgram}, sección de {evaluations},
      botón "Copiar {evaluations}" y luego seleccionar "Importar/Exportar a otro Perfil de Egreso".
    </p>

    <h3>2.2 Copiar entre {egressProfileMatters} y {baseMatter} ({egressProfileMatters} a {baseMatter})</h3>
    <p>
      Permite copiar {evaluations} entre la asignatura actual (dentro de un perfil de egreso) y su
      {baseMatter}. Esto es útil para sincronizar {evaluations} entre la plantilla base y las asignaturas derivadas.
    </p>
    <p>
      <strong>Dónde se encuentra:</strong> En la vista de {matterProgram}, sección de {evaluations},
      botón "Copiar {evaluations}" y luego seleccionar "Importar/Exportar a {baseMatter}".
    </p>
    <p><strong>Condiciones de visibilidad:</strong></p>
    <ul>
      <li>El usuario debe tener permisos de edición y ser super usuario.</li>
    </ul>

    <h3>2.3 Copiar desde {baseMatter} hacia {egressProfileMatters} ({baseMatter} a {egressProfileMatters})</h3>
    <p>
      Permite copiar {evaluations} desde la {baseMatter} hacia una asignatura de perfil de egreso. Esta
      operación se accede desde la vista de la {baseMatter} cuando existe un contexto de perfil de egreso asociado.
    </p>
    <p><strong>Condiciones de visibilidad:</strong></p>
    <ul>
      <li>El usuario debe tener permisos de edición y ser super usuario.</li>
      <li>Debe existir al menos otra asignatura del mismo tipo no vinculada en otro perfil de egreso.</li>
    </ul>

    <h3>Resumen de operaciones</h3>
    <table border="1" cellpadding="6" cellspacing="0" style="border-collapse:collapse;width:100%">
      <thead>
        <tr>
          <th>Operación</th>
          <th>Origen por defecto</th>
          <th>Destino por defecto</th>
          <th>Requiere super usuario</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td>{egressProfileMatters} a {egressProfileMatters}</td>
          <td>Asignatura actual ({egressProfileMatters})</td>
          <td>Otra asignatura de otro Perfil de Egreso</td>
          <td>Sí</td>
        </tr>
        <tr>
          <td>{egressProfileMatters} a {baseMatter}</td>
          <td>Asignatura actual ({egressProfileMatters})</td>
          <td>{baseMatter}</td>
          <td>Sí</td>
        </tr>
        <tr>
          <td>{baseMatter} a {egressProfileMatters}</td>
          <td>{baseMatter}</td>
          <td>Asignatura de un Perfil de Egreso</td>
          <td>Sí</td>
        </tr>
      </tbody>
    </table>

    <h2>3. Interfaz del Modal de Copia</h2>
    <p>
      El modal de copia presenta una interfaz de tres columnas que permite visualizar y controlar el proceso
      de copia en tiempo real.
    </p>

    <h3>3.1 Columna de Origen ({evaluations} a Importar/Exportar)</h3>
    <p>Muestra las {evaluations} disponibles en la asignatura de origen, ordenadas alfabéticamente.</p>
    <ul>
      <li>Cada evaluación muestra su nombre, ponderación (%) y los instrumentos asociados.</li>
      <li>El usuario selecciona con checkboxes las {evaluations} que desea copiar al destino.</li>
      <li>Botones de "Seleccionar todos" y "Deseleccionar todos" para agilizar la selección.</li>
      <li>Si una evaluación tiene instrumentos vinculados, estos se muestran como una lista debajo del nombre.</li>
    </ul>

    <h3>3.2 Columna de Destino ({evaluations} a Actualizar)</h3>
    <p>Muestra las {evaluations} que actualmente existen en la asignatura de destino.</p>
    <ul>
      <li>El usuario puede seleccionar {evaluations} del destino para eliminarlas como parte de la operación.</li>
      <li>Las {evaluations} no seleccionadas se conservan y aparecen en la vista previa.</li>
      <li>Si no se selecciona ninguna evaluación de destino, la copia solo agrega {evaluations} nuevas sin eliminar nada.</li>
    </ul>

    <h3>3.3 Columna de Vista Previa del Resultado</h3>
    <p>
      Muestra en tiempo real cómo quedará la asignatura de destino después de aplicar la copia. Combina las
      {evaluations} que se conservan del destino con las nuevas que se agregan desde el origen.
    </p>
    <ul>
      <li>Las {evaluations} nuevas (provenientes del origen) se muestran en color verde y texto en negrita.</li>
      <li>Las {evaluations} existentes que se conservan se muestran en texto normal.</li>
      <li>Cada evaluación tiene un campo de ponderación editable que permite ajustar el porcentaje antes de confirmar.</li>
      <li>Al pie de la columna se muestra el total de ponderaciones y se valida que no supere el 100%.</li>
    </ul>

    <h2>4. Funcionalidad Bidireccional (Invertir dirección)</h2>
    <p>
      Todas las operaciones de copia son bidireccionales. En la parte superior del modal se muestra la
      dirección actual de la copia junto con un botón para invertir la dirección.
    </p>
    <p>
      Al presionar "Invertir dirección", la asignatura que era origen pasa a ser destino y viceversa.
    </p>
    <ul>
      <li>Al invertir la dirección se reinician las selecciones y las ponderaciones ajustadas.</li>
      <li>La descripción del modal se actualiza automáticamente para reflejar la nueva dirección.</li>
      <li>Para la copia {egressProfileMatters} a {baseMatter}, el modal muestra una alerta amarilla (advertencia) dado que modificar la {baseMatter} puede impactar a múltiples asignaturas derivadas.</li>
    </ul>

    <h2>5. Selector de Asignatura Destino</h2>
    <p>
      En las operaciones {egressProfileMatters} a {egressProfileMatters} y {baseMatter} a {egressProfileMatters},
      el modal incluye un selector desplegable en la parte superior para elegir la asignatura de destino.
    </p>
    <ul>
      <li>El selector muestra todas las asignaturas del mismo tipo en otros perfiles de egreso.</li>
      <li>Se excluye la asignatura actual y las que tienen la opción "Vincular Asignatura en Sí" activada.</li>
      <li>Mientras no se seleccione una asignatura, el botón de "Copiar {evaluations}" permanece deshabilitado.</li>
      <li>Al seleccionar una asignatura, se cargan automáticamente sus {evaluations} en la columna de destino.</li>
    </ul>
    <p>
      En la operación {egressProfileMatters} a {baseMatter}, no se necesita selector ya que la {baseMatter}
      se determina automáticamente por el contexto.
    </p>

    <h2>6. Validación de Ponderaciones</h2>
    <p>
      El sistema valida que la suma total de las ponderaciones en la vista previa no supere el 100%. Si se
      excede, se muestra un mensaje de error en rojo y el botón de confirmación se deshabilita.
    </p>
  `,
};
